import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// import apiClient from '../../services/apiClient'; // Removed unused import
import { useAuth } from '../../contexts/AuthContext';

/* // Commenting out LineItemRow
const LineItemRow = ({ item, index, onChange, onRemove }) => {
  return (
    <div className="grid grid-cols-4 gap-4 mb-2 items-center">
      <input 
        type="text"
        placeholder="Description"
        name="description"
        value={item.description}
        onChange={(e) => onChange(index, e)}
        className="col-span-2 p-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-green-medium focus:border-brand-green-medium"
      />
      <input 
        type="number"
        placeholder="Quantity"
        name="quantity"
        value={item.quantity}
        onChange={(e) => onChange(index, e)}
        className="p-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-green-medium focus:border-brand-green-medium"
      />
      <input 
        type="number"
        placeholder="Unit Price"
        name="unitPrice"
        value={item.unitPrice}
        onChange={(e) => onChange(index, e)}
        className="p-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-green-medium focus:border-brand-green-medium"
      />
      <button 
        type="button" 
        onClick={() => onRemove(index)} 
        className="text-red-500 hover:text-red-700 font-semibold"
      >
        Remove
      </button>
    </div>
  );
};
*/

const NewInvoicePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const initialLineItem = { description: '', quantity: 1, unitPrice: 0, total: 0 };

  const [formData, setFormData] = useState({
    clientId: null,
    originatingEstimateId: null,
    originatingQuoteId: null,
    invoiceNumber: `INV-${Date.now()}`,
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    lineItems: [initialLineItem],
    subTotal: 0,
    taxRate: 0.0825,
    taxAmount: 0,
    totalAmount: 0,
    status: 'draft',
    notes: '',
    termsAndConditions: '',
    includeInTaxReport: true,
    amountPaid: 0,
    paymentDate: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSourceLoading, setIsSourceLoading] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [isConverting, setIsConverting] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      if (!token) return;
      try {
        const response = await fetch('/api/users?role=client', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch clients');
        const data = await response.json();
        setClients(data.data.users || []);
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError('Failed to load clients. Please try again.');
      }
    };
    fetchClients();

    if (location.state && location.state.estimateToConvert) {
      const estimate = location.state.estimateToConvert;
      setIsConverting(true);
      setFormData(prev => ({
        ...prev,
        clientId: estimate.clientId || estimate.client?.id || null,
        originatingEstimateId: estimate.id,
        originatingQuoteId: estimate.originatingQuoteId || null,
        lineItems: estimate.lineItems?.map(item => ({ 
            description: item.description, 
            quantity: parseFloat(item.quantity || 1),
            unitPrice: parseFloat(item.unitPrice || 0),
            total: parseFloat(item.total || 0)
        })) || [initialLineItem],
        notes: estimate.notes || '',
        termsAndConditions: estimate.termsAndConditions || '',
        taxRate: parseFloat(estimate.taxRate || 0.0825),
      }));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [token, location, navigate]);

  useEffect(() => {
    let currentSubTotal = 0;
    formData.lineItems.forEach(item => {
      currentSubTotal += parseFloat(item.total || 0);
    });
    const currentTaxAmount = currentSubTotal * parseFloat(formData.taxRate || 0);
    const currentTotalAmount = currentSubTotal + currentTaxAmount;
    setFormData(prev => ({
      ...prev,
      subTotal: currentSubTotal,
      taxAmount: currentTaxAmount,
      totalAmount: currentTotalAmount,
    }));
  }, [formData.lineItems, formData.taxRate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value),
    }));
  };

  const handleLineItemChange = (index, e) => {
    const { name, value, type } = e.target;
    const updatedLineItems = [...formData.lineItems];
    const item = updatedLineItems[index];
    item[name] = type === 'number' ? parseFloat(value) : value;
    if (name === 'quantity' || name === 'unitPrice') {
      item.total = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
    }
    updatedLineItems[index] = item;
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: 1, unitPrice: 0, total: 0 }],
    }));
  };

  const removeLineItem = (index) => {
    const updatedLineItems = [...formData.lineItems];
    updatedLineItems.splice(index, 1);
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (!formData.clientId) {
      setError('Please select a client.');
      setIsLoading(false);
      return;
    }
    if (!formData.dueDate) {
      setError('Due date is required.');
      setIsLoading(false);
      return;
    }
    if (formData.lineItems.some(item => !item.description || item.quantity <= 0 || item.unitPrice < 0)) {
      setError('Please ensure all line items have a description and positive quantity.');
      setIsLoading(false);
      return;
    }

    const payload = {
      ...formData,
      clientId: parseInt(formData.clientId, 10),
      invoiceDate: new Date(formData.invoiceDate).toISOString(),
      dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
      amountPaid: parseFloat(formData.amountPaid || 0),
      includeInTaxReport: formData.includeInTaxReport,
    };

    console.log('Submitting Invoice:', payload);

    try {
      const response = await fetch('/api/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to create invoice');
      }
      alert('Invoice created successfully!');
      if (responseData.data && responseData.data.invoice && responseData.data.invoice.id) {
        navigate(`/admin/invoices/view/${responseData.data.invoice.id}`);
      } else {
        navigate('/admin/invoices');
      }
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.message || 'An error occurred while creating the invoice.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
  const inputSmClass = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";

  if (isSourceLoading) {
      return <div className="container mx-auto px-4 py-8 text-center">Loading details from source...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-brand-text-dark">
          {isConverting ? 'Convert Estimate to Invoice' : 'Create New Invoice'}
        </h1>
        <button onClick={() => navigate(-1)} className="text-sm text-blue-600 hover:text-blue-800 mt-2">&larr; Back</button>
      </header>

      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6 md:p-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong><span className="block sm:inline">{error}</span>
          </div>
        )}

        <div className="mb-6">
          <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">Client</label>
          <select name="clientId" id="clientId" value={formData.clientId || ''} onChange={handleChange} className={inputClass} required disabled={!clients.length || !!formData.originatingEstimateId }>
            <option value="" disabled>Select a client</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.username} ({client.email})</option>
            ))}
          </select>
          {formData.originatingEstimateId && <p className="text-xs text-gray-500 mt-1">Client selected from originating Estimate.</p>}
        </div>
        
        {formData.originatingEstimateId && (
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">Converted from Estimate ID: {formData.originatingEstimateId}</p>
            </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
            <input type="text" name="invoiceNumber" id="invoiceNumber" value={formData.invoiceNumber} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="invoiceDate" className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
            <input type="date" name="invoiceDate" id="invoiceDate" value={formData.invoiceDate} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input type="date" name="dueDate" id="dueDate" value={formData.dueDate} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select name="status" id="status" value={formData.status} onChange={handleChange} className={inputClass}>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="partially_paid">Partially Paid</option>
              <option value="overdue">Overdue</option>
              <option value="void">Void</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Line Items</h3>
          {formData.lineItems.map((item, index) => (
            <div key={index} className="grid grid-cols-12 gap-x-3 gap-y-2 items-end p-3 mb-3 border rounded-md">
              <div className="col-span-12 sm:col-span-5">
                <label htmlFor={`itemDesc-${index}`} className="block text-xs font-medium text-gray-600">Description</label>
                <input type="text" name="description" id={`itemDesc-${index}`} value={item.description} onChange={(e) => handleLineItemChange(index, e)} className={inputSmClass} placeholder="Service or Item" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemQty-${index}`} className="block text-xs font-medium text-gray-600">Quantity</label>
                <input type="number" name="quantity" id={`itemQty-${index}`} value={item.quantity} onChange={(e) => handleLineItemChange(index, e)} className={`${inputSmClass} text-right`} min="0.01" step="0.01" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemPrice-${index}`} className="block text-xs font-medium text-gray-600">Unit Price</label>
                <input type="number" name="unitPrice" id={`itemPrice-${index}`} value={item.unitPrice} onChange={(e) => handleLineItemChange(index, e)} className={`${inputSmClass} text-right`} min="0.00" step="0.01" required />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label htmlFor={`itemTotal-${index}`} className="block text-xs font-medium text-gray-600">Item Total</label>
                <input type="number" name="total" id={`itemTotal-${index}`} value={item.total.toFixed(2)} readOnly className={`${inputSmClass} text-right bg-gray-50`} />
              </div>
              <div className="col-span-12 sm:col-span-1 flex justify-end">
                {formData.lineItems.length > 1 && (
                  <button type="button" onClick={() => removeLineItem(index)} className="text-red-500 hover:text-red-700 p-1 rounded-md text-sm">Remove</button>
                )}
              </div>
            </div>
          ))}
          <button type="button" onClick={addLineItem} className="mt-2 text-blue-600 hover:text-blue-800 font-medium text-sm py-2 px-3 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors">
            + Add Line Item
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 border-t pt-6 mt-6">
          <div>
            <label htmlFor="subTotal" className="block text-sm font-medium text-gray-500">Subtotal</label>
            <input type="text" name="subTotal" id="subTotal" value={formData.subTotal.toFixed(2)} readOnly className={`${inputClass} bg-gray-50 text-right`} />
          </div>
          <div>
            <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700">Tax Rate</label>
            <input type="number" name="taxRate" id="taxRate" value={formData.taxRate} onChange={handleChange} className={`${inputClass} text-right`} step="0.0001" />
          </div>
          <div>
            <label htmlFor="taxAmount" className="block text-sm font-medium text-gray-500">Tax Amount</label>
            <input type="text" name="taxAmount" id="taxAmount" value={formData.taxAmount.toFixed(2)} readOnly className={`${inputClass} bg-gray-50 text-right`} />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 border-t pt-6">
          <div>
            <label htmlFor="amountPaid" className="block text-sm font-medium text-gray-700">Amount Paid</label>
            <input type="number" name="amountPaid" id="amountPaid" value={formData.amountPaid} onChange={handleChange} className={`${inputClass} text-right`} step="0.01" min="0"/>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Balance Due</p>
            <p className="text-2xl font-bold text-gray-800">${(formData.totalAmount - formData.amountPaid).toFixed(2)}</p>
          </div>
        </div>

        <div className="text-right mb-6 border-t pt-4">
          <p className="text-sm text-gray-500">Total Amount</p>
          <p className="text-2xl font-bold text-gray-800">${formData.totalAmount.toFixed(2)}</p>
        </div>

        <div className="mb-6">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
          <textarea name="notes" id="notes" value={formData.notes} onChange={handleChange} rows="3" className={inputClass} placeholder="Internal notes or notes for the client..."></textarea>
        </div>

        <div className="mb-6">
          <label htmlFor="termsAndConditions" className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions (Optional)</label>
          <textarea name="termsAndConditions" id="termsAndConditions" value={formData.termsAndConditions} onChange={handleChange} rows="3" className={inputClass} placeholder="Define payment terms, payment methods, etc..."></textarea>
        </div>
        
        <div className="mb-8">
          <label htmlFor="includeInTaxReport" className="flex items-center">
            <input 
              type="checkbox" 
              name="includeInTaxReport" 
              id="includeInTaxReport"
              checked={formData.includeInTaxReport}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Include this invoice in tax reporting</span>
          </label>
        </div>
        
        <div className="flex justify-end pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/admin/invoices')}
            className="mr-3 px-6 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={isLoading || isSourceLoading}
          >
            {isLoading ? 'Creating...' : 'Create Invoice'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewInvoicePage;
